import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Button, InputAdornment, Icon, Typography, FormControlLabel, Checkbox, Tooltip, DialogTitle, DialogContent } from '@material-ui/core';
import { Link } from 'react-router-dom';
import { TextFieldFormsy } from '@fuse';
import Formsy from 'formsy-react';
import * as authActions from 'app/auth/store/actions';
import { useDispatch, useSelector } from 'react-redux';
import Error from 'app/fuse-layouts/shared-components/Error';
import * as ActionsDialog from 'app/store/actions';
import secureStorage from './utils/secureStorage';
import { API_BASE_URL } from '@config';

// Blocking error codes that should show a non-dismissable modal
const BLOCKING_ERROR_CODES = ['ORGANIZATION_SUSPENDED', 'LICENSE_EXPIRED', 'ORGANIZATION_NOT_FOUND'];

// Blocking error dialog component (no close button, cannot be dismissed)
const BlockingErrorDialog = ({ title, message }) => (
	<>
		<DialogTitle>
			<Typography className="font-bold text-red">{title}</Typography>
		</DialogTitle>
		<DialogContent>
			<Typography>{message}</Typography>
		</DialogContent>
	</>
);

const LocalLoginForm = props => {
	const dispatch = useDispatch();
	const formRef = useRef(null);

	const error = useSelector(({ auth }) => auth.auth.error);
	const licenseType = useSelector(({ licenseKey }) => licenseKey.key.licenseType);

	const [isFormValid, setIsFormValid] = useState(false);
	const [verify, setVerify] = useState(null);
	const [changed, setChanged] = useState(false);
	const [showPassword, setShowPassword] = useState(false);
	const [rememberMe, setRememberMe] = useState(false);
	const [storedCredentials, setStoredCredentials] = useState(null);
	// When the identifier resolves to a portal-sso user, swap the password
	// form for a redirect back to the portal. Dismissing returns to the form
	// so admins who set a local password can still sign in here directly.
	const [ssoHint, setSsoHint] = useState(null);
	const [ssoDismissed, setSsoDismissed] = useState(false);

	useEffect(() => {
		if (error) {
			if (formRef.current && error.type === 'form') {
				let message = error.message;
				if (error === 'Unauthorized') message = error;
				const location = { username: message, password: message };
				formRef.current.updateInputsWithError({
					...location
				});
				disableButton();
			} else if (error.type === 'modal' && error.message) {
				// Determine title based on error code
				const blockingCodes = ['ORGANIZATION_SUSPENDED', 'LICENSE_EXPIRED', 'ORGANIZATION_NOT_FOUND'];
				const isBlockingError = blockingCodes.includes(error.code);
				const title = isBlockingError ? 'Access Denied' : undefined;

				dispatch(
					ActionsDialog.openDialog({
						children: <Error message={error.message} title={title} clearError={() => dispatch(authActions.clearError())} />,
						maxWidth: 'xs',
						fullWidth: true,
						onClose: (event, reason) => {
							if (reason !== 'backdropClick') {
								dispatch(ActionsDialog.closeDialog());
							}
						}
					})
				);
			}
		}
	}, [dispatch, error, verify, licenseType]);

	useEffect(() => {
		if (!verify && props.history.location.state === 'verify') {
			setVerify(props.history.location.state);
			props.history.push({ state: null });
		}
	}, [verify, props.history, licenseType]);

	useEffect(() => {
		if (error && !changed) {
			dispatch(authActions.clearError());
		}
	}, [dispatch, error, changed]);

	// Load stored credentials on component mount
	useEffect(() => {
		const loadStoredCredentials = () => {
			try {
				const credentials = secureStorage.getStoredCredentials();
				if (credentials) {
					setStoredCredentials(credentials);
					setRememberMe(true);
					
					// Pre-fill the form with stored credentials
					// Use a small delay to ensure Formsy is ready
					setTimeout(() => {
						if (formRef.current) {
							try {
								formRef.current.updateInputsWithValue({
									username: credentials.username,
									password: credentials.password
								});
							} catch (error) {
								console.warn('Could not pre-fill form, will set default values instead:', error);
							}
						}
					}, 100);
				} 
			} catch (error) {
				console.error('Error loading stored credentials:', error);
			}
		};

		// Only load credentials if not in password recovery mode
		if (!props.recoverPassword) {
			loadStoredCredentials();
		}
	}, [props.recoverPassword]);

	const disableButton = () => setIsFormValid(false);

	const enableButton = () => setIsFormValid(true);

	const checkAuthHint = async (identifier) => {
		const id = (identifier || '').trim();
		if (!id || id.length < 4) return;
		try {
			const { data } = await axios.post(`${API_BASE_URL}/api/auth/hint`, {
				username: id,
				email: id,
			});
			if (data && data.authType === 'portal-sso') {
				setSsoHint(data);
			}
		} catch (_) {
			// Any network / server error just falls back to the password form.
		}
	};

	const handleSubmit = model => {
		if (props.recoverPassword) {
			// Password recovery mode - model contains 'email'
			dispatch(authActions.recoverPassword(model));
			props.setRecoverPassword(!props.recoverPassword);
		} else {
			// Normal login mode - model contains 'username' and 'password'
			const data = {
				...model,
				username: model.username.toLowerCase()
			};

			// Handle Remember Me functionality
			if (rememberMe && model.username && model.password) {
				const success = secureStorage.storeCredentials({
					username: model.username.toLowerCase(),
					password: model.password,
					rememberDuration: 168 // 7 days
				});
			} else if (!rememberMe) {
				// Clear stored credentials if remember me is unchecked
				secureStorage.clearStoredCredentials();
			}

			disableButton();
			setChanged(false);
			dispatch(authActions.submitLocalLogin(data));
		}
	};

	if (ssoHint && !ssoDismissed && !props.recoverPassword) {
		const portalUrl = ssoHint.portalLoginUrl || 'https://heidiai.io';
		const nextUrl = typeof window !== 'undefined' ? window.location.href : '';
		const loginHref = `${portalUrl}/login?next=${encodeURIComponent(nextUrl)}`;
		return (
			<div className="w-full">
				<Typography variant="h6" className="text-center md:w-full mb-24">
					Sign in via heidiai.io
				</Typography>
				<Typography variant="body2" className="text-center mb-32">
					This account signs in through the Heidi portal. Click below to continue.
				</Typography>
				<Button
					variant="contained"
					color="primary"
					className="w-full mx-auto mb-16"
					href={loginHref}
				>
					Continue to heidiai.io
				</Button>
				<Button
					variant="text"
					size="small"
					className="w-full mx-auto"
					onClick={() => { setSsoDismissed(true); setSsoHint(null); }}
				>
					Use a local password instead
				</Button>
			</div>
		);
	}

	return (
		<div className="w-full">
			<Typography variant="h6" className="text-center md:w-full mb-48">
				{props.recoverPassword ? 'Recover Password' : 'Sign in to your account'}
			</Typography>
			<Formsy
				onValidSubmit={handleSubmit}
				onValid={enableButton}
				onInvalid={disableButton}
				onChange={() => setChanged(true)}
				ref={formRef}
				className="flex flex-col justify-center w-full"
			>
				{!props.recoverPassword && (
					<React.Fragment>
						<TextFieldFormsy
							className="mb-16"
							type="text"
							name="username"
							id="login_form_username"
							label="Username"
							value={storedCredentials ? storedCredentials.username : ""}
							onBlur={e => !ssoDismissed && checkAuthHint(e.target.value)}
							validations={{
								minLength: 4
							}}
							validationErrors={{
								minLength: 'Min character length is 4'
							}}
							InputProps={{
								endAdornment: (
									<InputAdornment position="end">
										<Icon className="text-20" color="action">
											email
										</Icon>
									</InputAdornment>
								)
							}}
							variant="outlined"
							required
						/>

						<TextFieldFormsy
							className="mb-8"
							type={showPassword ? "text" : "password"}
							name="password"
							id="login_form_password"
							label="Password"
							value={storedCredentials ? storedCredentials.password : ""}
							validations={{
								minLength: 4
							}}
							validationErrors={{
								minLength: 'Min character length is 4'
							}}
							InputProps={{
								endAdornment: (
									<InputAdornment position="end">
										<Icon 
											className="text-20 cursor-pointer" 
											color="action"
											onClick={() => setShowPassword(!showPassword)}
										>
											{showPassword ? 'visibility_off' : 'visibility'}
										</Icon>
									</InputAdornment>
								)
							}}
							variant="outlined"
							required
						/>

						{/* Remember Me Section - positioned near password field, aligned right */}
						<div className="flex justify-end mb-16">
							<FormControlLabel
								control={
									<Checkbox
										checked={rememberMe}
										onChange={(e) => {
											setRememberMe(e.target.checked);
										}}
										color="primary"
										size="small"
									/>
								}
								label={<Typography variant="body2">Remember my credentials</Typography>}
							/>
						</div>
					</React.Fragment>
				)}

				{verify && !props.recoverPassword && (
					<TextFieldFormsy
						id="login_form_userKey"
						className="mb-16"
						label="User Key"
						autoFocus
						type="textarea"
						name="userKey"
						value={props.match.params.userKey || ''}
						multiline
						minRows={5}
						variant="outlined"
						required
						fullWidth
					/>
				)}

				{props.recoverPassword && (
					<TextFieldFormsy
						className="mb-16"
						type="email"
						name="email"
						id="login_form_email"
						label="Email Address"
						validations={{
							isEmail: true
						}}
						validationErrors={{
							isEmail: 'Please enter a valid email address'
						}}
						InputProps={{
							endAdornment: (
								<InputAdornment position="end">
									<Icon className="text-20" color="action">
										email
									</Icon>
								</InputAdornment>
							)
						}}
						variant="outlined"
						required
					/>
				)}

				<Button
					type="submit"
					variant="contained"
					id="login_form_submit"
					color="primary"
					className="w-full mx-auto mt-16"
					aria-label="Sign in"
					disabled={!isFormValid}
					value="legacy"
				>
					{props.recoverPassword ? 'Submit' : 'Sign in'}
				</Button>
			</Formsy>
			<div className="flex flex-col items-center justify-center pt-32">
				<Link className="font-medium" to="#" onClick={() => props.setRecoverPassword(!props.recoverPassword)}>
					{!props.recoverPassword ? 'Forgot password?' : 'Back to sign in'}
				</Link>
				<Link className="font-medium mt-4" to="/register">
					Create an account
				</Link>
			</div>
		</div>
	);
};

export default LocalLoginForm;
